import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { match } from "ts-pattern";
import Markdown from "markdown-to-jsx";

const Message = styled.div`
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 4px;
  background-color: #f1f1f1;
  border: 0.2px solid ${({ theme }) => theme.primaryBorder};
  max-width: 92%;
  font-size: 13px;
  line-height: 19px;
`;

const UserMessage = styled(Message)`
  align-self: flex-end;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_brand_mixed")};
  border: 0.2px solid ${({ theme }) => theme.brandColor};
  /* #0013a3; */
`;

const AIMessage = styled(Message)`
  align-self: flex-start;
  background-color: white;
  && p:first-child {
    margin-block-start: 0;
    margin-top: 0;
  }
  && p:last-child {
    margin-block-end: 0;
    margin-bottom: 0;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
  font-size: 1em;
  & th,
  & td {
    border: 1px solid ${({ theme }) => theme.secondaryBorder};
    padding: 8px;
    text-align: left;
  }
  & th {
    background-color: #f2f2f2;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
`;

type TextContent = {
  value: string; // Markdown
  // annotations: Annotation[];
};

type Content = {
  type: "text";
  text: TextContent;
};

export type ChatMessage = {
  id: string;
  run_id: string | null;
  role: "assistant" | "user";
  created_at: string | null;
  completed_at: string | null;
  content: Content[];
  status: string | null;
  thread_id: string;
};

type MessageTimelineProps = {
  messages: ChatMessage[];
  contentRef: React.RefObject<HTMLDivElement>;
};

export const MessageTimeline = ({
  messages,
  contentRef,
}: MessageTimelineProps) => {
  const [timelineMessages, setTimelineMessages] = useState<ChatMessage[]>(
    //@ts-ignore
    messages?.toReversed()
  );
  useEffect(() => {
    if (messages) {
      //@ts-ignore
      setTimelineMessages(messages?.toReversed());
    }
  }, [messages]);

  useEffect(() => {
    if (contentRef?.current && timelineMessages) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [contentRef, timelineMessages]);

  return (
    <>
      {timelineMessages.map((message, index) =>
        match(message.role)
          .with("user", () => (
            <UserMessage key={index}>
              {message.content.map((content, index) => (
                <div style={{ wordBreak: "break-all" }} key={index}>
                  {content.text.value}
                </div>
              ))}
            </UserMessage>
          ))
          .with("assistant", () => (
            <AIMessage key={index}>
              {/* If the user refreshes the page at an inopputune time thise may be undefined for the most recent message */}
              <Markdown
                options={{
                  overrides: {
                    a: {
                      props: {
                        target: "_blank",
                        rel: "noopener noreferrer",
                      },
                    },
                    table: {
                      component: (props: any) => (
                        <TableContainer>
                          <StyledTable {...props} />
                        </TableContainer>
                      ),
                    },
                  },
                }}
              >
                {message.content[0]?.text?.value ?? ""}
              </Markdown>
            </AIMessage>
          ))
          .exhaustive()
      )}
    </>
  );
};
